import styled from "@emotion/styled";
import React from "react";
import { observer } from "mobx-react-lite";
import { Column, Row } from "@components/Flex";
import { ReactComponent as BackIcon } from "@assets/icons/dubleArrowLeft.svg";
import Text from "@components/Text";
import SizedBox from "@components/SizedBox";
import { Link } from "react-router-dom";
import { ROUTES } from "@stores/AccountStore";
import { useDeviceCalibrationScreenVM } from "@screens/DeviceCalibrationScreen/DeviceCalibrationScreenVM";
import { useConvert, useLang, useLength } from "@src/hooks/useLang";
import { roundLengthForDisplay } from "@src/utils/convert";
import Input from "@components/Input";
import { MOBILE_BREAKPOINT } from "@src/hooks/useIsMobile";

interface IProps {}

const Root = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 16px 0;
  width: 290px;
  max-height: 100vh;
  border-right: 1px solid #cbd5d8;

  /* Phone: the screen stacks (see DeviceCalibrationScreen Root), so this stops
     being a sidebar and becomes the page header. A fixed 290px there left the
     table squeezed into whatever was left; the 100vh cap belongs to a column
     that scrolls on its own, which this no longer is. */
  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: 100%;
    max-height: none;
    padding: 12px 0;
    border-right: none;
    border-bottom: 1px solid #cbd5d8;
  }
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  padding: 0 16px;
`;

/* The device name is a heading on desktop and still the page title on a phone,
   but 24px of it wraps to three lines next to a 40px input. */
const DeviceName = styled.div`
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    > * {
      font-size: 20px;
    }
  }
`;

/* Reaper width is reference information, not something edited here — full
   width on desktop, but on a phone it is the only field and reads better
   capped than stretched edge to edge. */
const WidthField = styled.div`
  width: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    max-width: 240px;
  }
`;

const DeviceCalibrationMenu: React.FC<IProps> = () => {
  const vm = useDeviceCalibrationScreenVM();
  const lang = useLang();
  const { convertLength } = useConvert();
  const locLength = useLength();

  return (
    <Root>
      <Body>
        <Column crossAxisSize="max">
          <Link to={ROUTES.DEVICES}>
            <Row alignItems="center">
              <BackIcon style={{ marginRight: 4 }} />
              <Text size={12} color="#899093">
                {lang.devices.devicesTitle}
              </Text>
            </Row>
          </Link>
          <SizedBox height={24} />
          <DeviceName>
            <Text type="heading" color="#494C5B" size={24}>
              {vm.device?.harvester.name}
            </Text>
          </DeviceName>
          <SizedBox height={4} />
          <Text color="#899093" size={14}>
            {vm.device?.harvester.model}
          </Text>
          {/*<SizedBox height={24} />*/}
          {/*<DeviceTrackDetails />*/}
          {/*<SizedBox height={24} />*/}
          {/*<Text type="heading" color="#494C5B" size={14}>*/}
          {/*  {lang.devices.tagsListTitle}*/}
          {/*</Text>*/}
          {/*<SizedBox height={8} />*/}
          {/*<DeviceTagsList tags={[]} />*/}
          <SizedBox height={16} />
          <WidthField>
            <Input
              readOnly
              value={
                vm.device != null
                  ? roundLengthForDisplay(
                      convertLength(vm.device.harvester.default_reaper.width)
                    )
                  : ""
              }
              label={`${lang.devices.calibrationHeaderTitle} (${locLength()})`}
            />
          </WidthField>
          {/* No "Static device" toggle here: the flag is admin-only (admin
              EditDeviceDialog). A static device's calibrations all take the
              static path on the backend. */}
        </Column>
        {/*<Button kind="secondary">*/}
        {/*  {lang.devices.deviceSelectedTrackButton}*/}
        {/*</Button>*/}
      </Body>
    </Root>
  );
};
export default observer(DeviceCalibrationMenu);
